// Does the engine's baked CONTENT match the manifest it was handed?
//
// The installer image carries bytes: personas/, deploy/, brain-seeds/. The
// manifest names tree SHAs for those same bytes. Nothing has ever compared them
// — the two were kept in step by the self-update hop's version equality, which
// is a proxy, not a check.
//
// This is the ONLY thing the baked descriptor still asserts after this change,
// and it is the one thing the image is genuinely the authority on: it computed
// these SHAs from the tree it actually baked. Everything else the descriptor used
// to carry — image refs, digests, the platform version — was a photocopy of a pin
// the image had no part in choosing, and those are gone.
//
// WHY THIS LIVES IN THE CLI AND NOT IN THE ENGINE'S SHELL. The brain-seed
// comparison is a single opaque string with a specific format, and there are
// already two implementations of it that must agree byte for byte:
// `seedSetRevOf` in scripts/install-descriptor-gen.mjs (writes it) and
// `seedSetRev` in lib/platform-converge.ts (computes it from a manifest). A jq
// version inside installer/lib.sh would be a THIRD, in a third language, and
// keeping three copies of one string format in step is the exact duplication
// this whole change exists to remove. The engine shells out to this instead, so
// the comparison is made by the same code the converge rail already trusts.
import type { ReleaseManifest } from "./release-manifest.js";

/** The content block of a schemaVersion-2 install descriptor. */
export interface DescriptorContent {
  personas: Record<string, string>;
  infra: string;
  brainSeedsRev: string;
}

export interface CrossCheckProblem {
  field: string;
  baked: string;
  manifest: string;
}

/**
 * Compare the descriptor's content block against the manifest, field by field.
 * `[]` means they agree. Every disagreement names the field and BOTH values —
 * "the manifest and the image disagree" is not a message anyone can act on.
 *
 * Pure. The caller decides what a disagreement means; on the install rail it is
 * fatal.
 */
export function crossCheckContent(manifest: ReleaseManifest, content: DescriptorContent): CrossCheckProblem[] {
  const problems: CrossCheckProblem[] = [];

  const manifestInfra = manifest.components.infra?.treeSha ?? "";
  if (content.infra !== manifestInfra) {
    problems.push({ field: "infra", baked: content.infra, manifest: manifestInfra });
  }

  // ── PERSONAS AND SEEDS: COMPARED BY VALUE, ON WHAT THE TWO SHARE ───────────
  //
  // NOT set equality, and this is the correction that matters. The two documents
  // describe DIFFERENT SETS BY DESIGN, and demanding they match refuses every
  // real install:
  //
  //   descriptor.content.personas  = the DEFAULT-INSTALL roster (pin.personas).
  //                                  On 0.7.8: ["ezra"].
  //   manifest…personas.items      = the FULL RELEASE roster.
  //                                  On 0.7.8: ["cmo", "ezra", "startup-advisor"].
  //
  // MEASURED against the published 0.7.8 pair, not reasoned about: `infra` and
  // the seed rev agree byte for byte, and only the persona SETS differ. The first
  // version of this function required every manifest persona to be present in the
  // image, and would have refused a healthy 0.7.8 install outright — caught by
  // running the shipped engine against the real published manifest, which is the
  // one thing no unit test here was able to say.
  //
  // So the question this asks is: **for everything this image will actually
  // deploy, does the release agree about its bytes?** Something the manifest
  // names and the image does not carry is not a mis-install — the default
  // install never reaches it. Something they BOTH name with DIFFERENT bytes is
  // exactly the defect this exists to catch.
  //
  // The canary manifest makes the same point from the other side: it names no
  // brain seeds at all while the image carries two. Equality would fail there
  // too, for a reason that has nothing to do with any byte being wrong.
  for (const [name, item] of Object.entries(manifest.components.personas.items)) {
    if (!Object.hasOwn(content.personas, name)) continue; // not in the default install
    if (content.personas[name] !== item.treeSha) {
      problems.push({ field: `personas.${name}`, baked: content.personas[name], manifest: item.treeSha });
    }
  }

  // The descriptor states seeds as ONE opaque string — `name:sub:sha` joined by
  // "|", written by seedSetRevOf in scripts/install-descriptor-gen.mjs. Parsing
  // it back is the honest inverse of writing it, and it is what lets this compare
  // entry by entry rather than all-or-nothing. It also makes the failure name the
  // seed that actually moved, instead of printing two 500-character strings and
  // leaving the reader to diff them by eye.
  const manifestSeeds = manifest.components.brainSeeds.items;
  for (const entry of content.brainSeedsRev.split("|")) {
    if (!entry) continue;
    // Split on the FIRST TWO colons only: an agent-sourced subtree value is
    // `sha256:<hex>`, which carries a colon of its own.
    const first = entry.indexOf(":");
    const second = entry.indexOf(":", first + 1);
    if (first < 0 || second < 0) continue;
    const name = entry.slice(0, first);
    const sub = entry.slice(first + 1, second);
    const baked = entry.slice(second + 1);
    if (!Object.hasOwn(manifestSeeds, name)) continue; // the release does not name this seed
    const subtrees = manifestSeeds[name].subtrees;
    if (!Object.hasOwn(subtrees, sub)) continue;
    const want = subtrees[sub] ?? "-";
    if (baked !== want) {
      problems.push({ field: `brainSeeds.${name}.${sub}`, baked, manifest: want });
    }
  }

  return problems;
}

/** Operator-facing text for a set of problems. Empty string when there are none. */
export function describeCrossCheckProblems(problems: CrossCheckProblem[], version: string): string {
  if (problems.length === 0) return "";
  const lines = problems.map((p) => `  ${p.field}\n    this image carries: ${p.baked}\n    ${version} names:    ${p.manifest}`);
  return (
    `This installer image does not carry the content that platform ${version} names.\n` +
    `${lines.join("\n")}\n\n` +
    "The install was stopped before anything was deployed. This is a fault in the release, not in your setup: " +
    "the engine and the manifest were built from different trees. Get the newest CLI with " +
    "`npm install -g @m8t-stack/cli` and start the install again."
  );
}
